import { useEffect, useState, useCallback } from "react";
import { IoMdArrowRoundBack, IoMdCart } from "react-icons/io";
import AssetsService from "../../rest/assetsService";
import MenuService from "../../rest/menuService";
import NoPictureWarning from "../../resources/nopic.png";
import { useMenuUpdates } from "../../ws/useMenuUpdates";

function KioskOrder({ cart, setCart, onBack, onReview }) {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 12;
    const clientCode = sessionStorage.getItem('clientCode');

    const fetchAllProducts = useCallback(async () => {
        let allProducts = [];
        let page = 0;
        let totalPages = 1;

        while (page < totalPages) {
            try {
                const resp = await MenuService.getCompleteActiveMenu({
                    search: "",
                    currentPage: page,
                    pageSize: 50,
                });

                const itemsWithImages = await Promise.all(
                    resp.content.map(async (item) => {
                        if (item.image_url) {
                            try {
                                const imageData = await AssetsService.getProductImage(item.image_url);
                                return { ...item, imageData };
                            } catch {
                                return { ...item, imageData: null };
                            }
                        }
                        return { ...item, imageData: null };
                    })
                );

                allProducts = [...allProducts, ...itemsWithImages];
                totalPages = resp.totalPages;
                page++;
            } catch (err) {
                console.error("Erro ao buscar produtos:", err);
                break;
            }
        }

        setProducts(allProducts);

        const cats = ["Todos"];
        allProducts.forEach((p) => {
            const cat = p.category?.trim() || "Outros";
            if (!cats.includes(cat)) cats.push(cat);
        });
        setCategories(
            cats.map((c) => ({
                id: c === "Todos" ? "all" : c === "Outros" ? "outros" : c.toLowerCase(),
                name: c
            }))
        );
    }, []);

    useEffect(() => {
        fetchAllProducts();
    }, [fetchAllProducts]);

    useMenuUpdates({ clientCode, onMenuUpdated: fetchAllProducts });

    const addToCart = (item) => {
        setCart(prev => ({
            ...prev,
            [item.id]: prev[item.id]
                ? { ...item, quantity: prev[item.id].quantity + 1 }
                : { ...item, quantity: 1 },
        }));
    };

    const removeFromCart = (id) => {
        setCart((prev) => {
            if (!prev[id]) return prev;
            const newCart = { ...prev };
            if (newCart[id].quantity === 1) delete newCart[id];
            else newCart[id].quantity -= 1;
            return newCart;
        });
    };

    const countItem = (id) => cart[id]?.quantity || 0;

    const total = Object.values(cart)
        .filter(item => item)
        .reduce((acc, item) => acc + (item.price || 0) * (item.quantity || 0), 0);


    const filteredProducts = products.filter((p) => {
        if (selectedCategory === "all") return true;
        if (selectedCategory === "outros") return !p.category || !p.category.trim();
        return (p.category?.trim().toLowerCase() || "") === selectedCategory;
    });

    const totalPages = Math.ceil(filteredProducts.length / pageSize);
    const startIndex = currentPage * pageSize;
    const pagedProducts = filteredProducts.slice(startIndex, startIndex + pageSize);

    return (
        <div className="h-screen flex flex-col bg-gray-50">
            <div className="flex items-center justify-between p-4 bg-custom_blue text-white shadow-lg">
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 text-lg font-semibold"
                >
                    <IoMdArrowRoundBack size={24} />
                    Voltar
                </button>
                <h1 className="text-2xl font-bold">Escolha seus itens</h1>
                <div className="flex items-center gap-2 text-lg font-semibold">
                    <IoMdCart size={24} />
                    R$ {total.toFixed(2)}
                </div>
            </div>

            <div className="flex gap-3 p-4 bg-white shadow-md overflow-x-auto">
                {categories.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => {
                            setSelectedCategory(cat.id);
                            setCurrentPage(0);
                        }}
                        className={`px-4 py-2 rounded-xl font-semibold transition ${selectedCategory === cat.id
                            ? "bg-custom_blue text-white"
                            : "bg-gray-200 text-gray-700"
                            }`}
                    >
                        {cat.name}
                    </button>
                ))}
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    {pagedProducts.map((prod) => {
                        const qty = countItem(prod.id);
                        return (
                            <div
                                key={prod.id}
                                className="bg-white rounded-2xl shadow-md hover:shadow-xl p-4 flex flex-col items-center text-center relative"
                            >
                                <img
                                    src={prod.imageData || prod.image || NoPictureWarning}
                                    alt={prod.name}
                                    className="w-32 h-32 object-cover rounded-xl mb-3"
                                />
                                <h2 className="font-bold text-lg">{prod.name}</h2>
                                <p className="text-custom_green text-xl font-semibold">
                                    R$ {prod.price.toFixed(2)}
                                </p>

                                <div className="flex items-center gap-3 mt-2">
                                    <button
                                        onClick={() => removeFromCart(prod.id)}
                                        disabled={qty === 0}
                                        className="px-3 py-1 bg-red-500 text-white rounded-lg disabled:bg-gray-300"
                                    >
                                        -
                                    </button>
                                    <span className="text-lg font-bold">{qty}</span>
                                    <button
                                        onClick={() => addToCart(prod)}
                                        className="px-3 py-1 bg-custom_green text-white rounded-lg"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {totalPages > 1 && (
                    <div className="flex justify-center gap-4 mt-6">
                        <button
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                            disabled={currentPage === 0}
                            className="px-4 py-2 bg-gray-200 rounded"
                        >
                            Anterior
                        </button>
                        <span className="px-4 py-2">
                            Página {currentPage + 1} de {totalPages}
                        </span>
                        <button
                            onClick={() =>
                                setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))
                            }
                            disabled={currentPage + 1 >= totalPages}
                            className="px-4 py-2 bg-gray-200 rounded"
                        >
                            Próxima
                        </button>
                    </div>
                )}
            </div>

            <div className="p-4 bg-white shadow-inner">
                <button
                    disabled={total === 0}
                    onClick={() => onReview(cart)}
                    className="w-full py-4 bg-custom_green text-white text-2xl font-bold rounded-xl shadow-lg disabled:bg-gray-400 transition-transform hover:scale-105 active:scale-95"
                >
                    Revisar Pedido
                </button>
            </div>
        </div>
    );
}

export default KioskOrder;
